import { computed, nextTick, ref } from 'vue'

/**
 * Collects callbacks (actions) from different places (e.g. components)
 * and runs them all at once, in the order they were added.
 *
 * Designed to be spread into a Pinia store so each store gets its own
 * independent stack:
 *
 *   export const useXxxStore = defineStore('xxxStore', () => {
 *     const stackedActions = useStackedActions()
 *
 *     return { ...stackedActions }
 *   })
 *
 * Actions can optionally be identified by a key — adding an action with
 * an already existing key overwrites the previous one in place (keeps
 * its original position in the queue). Keyless actions always append.
 *
 * Supports async actions — `runActions` awaits each one sequentially.
 */
export const useStackedActions = () => {
  const actions = ref([]) // Array of { key, action }

  const hasActions = computed(() => actions.value.length > 0)

  const hasAction = key => actions.value.some(item => item.key === key)

  const addAction = (action, key = null) => {
    if (typeof action !== 'function')
      return

    if (key) {
      const existingIndex = actions.value.findIndex(item => item.key === key)

      if (existingIndex !== -1) {
        actions.value[existingIndex] = { key, action }

        return
      }
    }

    actions.value.push({ key, action })
  }

  const removeAction = key => {
    actions.value = actions.value.filter(item => item.key !== key)
  }

  const clearActions = () => {
    actions.value = []
  }

  /**
   * Runs all queued actions sequentially and clears the queue.
   * Pass `tickBetween: true` when actions depend on the DOM being
   * updated after the previous one (e.g. FullCalendar interactions).
   */
  const runActions = async ({ tickBetween = false } = {}) => {
    const actionsToRun = [...actions.value]

    clearActions()

    for (const { action } of actionsToRun) {
      await action()
      if (tickBetween)
        await nextTick()
    }
  }

  return {
    actions,
    hasActions,
    hasAction,
    addAction,
    removeAction,
    runActions,
    clearActions,
  }
}
